import { useState, useRef, useEffect } from "react";
import { DayPicker } from "react-day-picker";
import "react-day-picker/dist/style.css";
import { format } from "date-fns";

interface DatePickerProps {
  value?: Date;
  onChange: (date: Date | undefined) => void;
  label?: string;
  placeholder?: string;
  disabledDates?: any; // To support react-day-picker disabled prop
  fromYear?: number;
  toYear?: number;
  defaultMonth?: Date;
}

const DatePicker: React.FC<DatePickerProps> = ({
  value,
  onChange,
  label = "Date",
  placeholder = "Select Date",
  disabledDates,
  fromYear,
  toYear,
  defaultMonth,
}) => {
  const [open, setOpen] = useState(false);
  const [month, setMonth] = useState<Date>(value || defaultMonth || new Date());
  const ref = useRef<HTMLDivElement>(null);

  useEffect(() => {
    if (value) setMonth(value);
  }, [value]);

  const formattedDate = value ? format(value, "dd MMM yyyy") : "";

  // close dropdown when clicking outside
  useEffect(() => {
    const handleClickOutside = (event: any) => {
      if (ref.current && !ref.current.contains(event.target)) {
        setOpen(false);
      }
    };
    document.addEventListener("mousedown", handleClickOutside);
    return () => document.removeEventListener("mousedown", handleClickOutside);
  }, []);

  const handleSelect = (date: Date | undefined) => {
    onChange(date);
    if (date) setOpen(false);
  };

  return (
    <>
      <div className="position-relative" ref={ref}>
        {/* BUTTON */}
        <button
          type="button"
          className="btn border bg-white w-100 fw-light btn-md text-start"
          onClick={() => setOpen(!open)}
        >
          <div className=" d-flex justify-content-between align-items-center">
            <div>
              <b className="fw-normal">{label}: {formattedDate || placeholder}</b>
            </div>
            <div className=" mt-1">
              <i className="fi fi-rr-angle-small-down me-0 fs-6 opacity-7"></i>
            </div>
          </div>
        </button>

        {/* CALENDAR */}
        {open && (
          <div
            className="shadow border bg-white border-radius-lg p-2 mt-2"
            style={{
              position: "absolute",
              zIndex: 1000,
              right: 0,
            }}
          >
            <DayPicker
              mode="single"
              selected={value}
              onSelect={handleSelect}
              captionLayout="dropdown"
              disabled={disabledDates}
              fromYear={fromYear || 1900}
              toYear={toYear || new Date().getFullYear()}
              month={month}
              onMonthChange={setMonth}
            />
          </div>
        )}
      </div>
    </>
  );
};

export default DatePicker;
